import React, { Suspense, useRef } from 'react';
import { IconTrash as DeleteIcon, IconCopy } from '@tabler/icons-react';

import { Canvas } from "@react-three/fiber";
import { View } from "@react-three/drei";

import { getStripeCheckoutURL } from "../api";
import {
  projectStyle,
  hiddenCanvas,
  getStoryboardChromeTheme,
  storyboardCardRootStyleFromTheme,
  editorModalPrimaryButtonStyles,
  editorModalSecondaryButtonStyles,
} from "../styleFile.js";

import { ScreenshotForProjectsPage } from "../components/Screenshot";

import { useObjectStore } from "../zustand/objects";

import { useAccountStore } from "../zustand/account";
import { Card, Button, Text, ActionIcon, Tooltip, Stack } from '@mantine/core';


export default function ProjectsTable({ projects, isGuest, handleCopySubmit }) {

    const { getSpecificUserProject, unloadObjects, objectsAreLoaded, viewportThemeOverride } = useObjectStore();
    const { updateDeleteModalOpen, updateDeleteID, userStatus } = useAccountStore();

    const isDark = viewportThemeOverride === "dark";
    const cardTheme = getStoryboardChromeTheme(isDark);
    const secondaryCardButtonStyles = {
        ...editorModalSecondaryButtonStyles,
        root: {
            ...editorModalSecondaryButtonStyles.root,
            color: isDark ? "var(--mantine-color-gray-0)" : "#111827",
        },
        label: {
            ...editorModalSecondaryButtonStyles.label,
            color: isDark ? "var(--mantine-color-gray-0)" : "#111827",
        },
    };

    const isLimitedPlan = userStatus === "loggedIn-none" || userStatus === "loggedIn-canceled";

    // Retrieve project data by url's query string (projectKey) and set to zustand project state
    const setProjectState = (projectKey) => {
        getSpecificUserProject(projectKey)
    }

    // Send projectKey to delete function and open delete modal
    const handleDeleteClick = (id) => {
        updateDeleteID(id)
        updateDeleteModalOpen()
    }

    // Wipe any object state and get new project data
    const onEditClick = (e) => {
        unloadObjects()
        setProjectState(e)
    }

    // As this page is for login redirection, this handles sucessful or failed subscription logins
    const handleSubscriptionClick = (label) => {
        getStripeCheckoutURL({
            success_url: 'https://mbxr.io/projects',
            cancel_url: 'https://mbxr.io/logging-out',
            // success_url: 'http://localhost:3000/projects',
            // cancel_url: 'http://localhost:3000/membership',
            subscription: label
        })
            .then((res) => window.location.replace(res.url))
    }

    const canvasRef = useRef()

    return (<div>
        <style>{`
          #projectsTableGrid {
            display: grid;
            gap: 16px;
            grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
          }
          .projectsTableCardRoot {
            transition: border-color 180ms ease, box-shadow 180ms ease;
          }
          .projectsTableCardRoot:hover {
            box-shadow:
              0 4px 12px rgba(15, 23, 42, 0.06),
              0 2px 4px rgba(15, 23, 42, 0.03) !important;
          }
          .projectsTableActionsRow {
            display: flex;
            flex-direction: row;
            align-items: center;
            width: 100%;
            padding: 10px;
            margin-top: 0;
            gap: 8px;
            box-sizing: border-box;
          }
          .projectsTableActionsMain { flex: 1; min-width: 0; }
          .projectsTableActionsMain .mantine-Button-root { width: 100%; max-width: 160px; margin: 0 !important; }
          .projectsTableActionsDelete { flex-shrink: 0; display: flex; justify-content: flex-end; }
          .projectsTableActionsDuplicate { flex-shrink: 0; display: flex; justify-content: flex-start; }
        `}</style>
        <Suspense fallback={null}>
            <Suspense fallback={<div>Loading... </div>}>
                <div>
                    <form style={projectStyle.container.slimFormContainer}>
                        <div id="container" style={{ marginTop: 0 }}>
                          <div id="projectsTableGrid">
                            {projects && projects.map((row, i) => (
                                row && objectsAreLoaded && <Suspense fallback={<img src="/image/loading.gif" alt="" style={projectStyle.loadingSpinnerContainer} />} key={i}>
                                    <div key={i}>
                                        <Card
                                            className="projectsTableCardRoot"
                                            radius={4}
                                            p={0}
                                            withBorder={false}
                                            styles={{
                                                root: {
                                                    ...storyboardCardRootStyleFromTheme(cardTheme),
                                                    boxShadow: isDark
                                                        ? "0 1px 2px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.3)"
                                                        : "0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 12px rgba(15, 23, 42, 0.04)",
                                                },
                                            }}
                                        >
                                            <div style={{
                                                ...projectStyle.box.projectScreenshotBox,
                                                backgroundColor: cardTheme.thumbBg,
                                                borderBottom: `1px solid ${cardTheme.cardBorder}`,
                                            }}>
                                                <Suspense fallback={<img src="/image/loading.gif" alt="" style={{ width: "50px", height: "50px" }} />}>
                                                    {objectsAreLoaded && <ScreenshotForProjectsPage index={i} floorColour={row.floorColour} artboard={row.artboards[1]} />}
                                                </Suspense>
                                            </div>
                                            <Stack gap={6} px={14} pt={14} pb={6} style={{ backgroundColor: cardTheme.cardContentMutedBg }}>
                                                <Text size="sm" fw={600} lineClamp={1} style={{ color: cardTheme.titleColor }}>
                                                    {row.projectName}
                                                </Text>
                                                <Text size="xs" fw={500} style={{ color: cardTheme.mutedTextColor }}>
                                                    {new Date(row.created_at).toDateString()}
                                                </Text>
                                            </Stack>
                                            <div className="projectsTableActionsRow" style={{ backgroundColor: cardTheme.cardContentMutedBg, borderTop: `1px solid ${cardTheme.cardBorder}` }}>
                                                <div className="projectsTableActionsMain">
                                                    {!isGuest && isLimitedPlan && i !== 0 ? (
                                                        <Button
                                                            onClick={() => handleSubscriptionClick("pioneer")}
                                                            radius={4}
                                                            styles={editorModalPrimaryButtonStyles}
                                                        >
                                                            Upgrade
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            onClick={() => { onEditClick(row.projectKey) }}
                                                            component="a"
                                                            href={`/editor/${row.projectKey}`}
                                                            radius={4}
                                                            styles={secondaryCardButtonStyles}
                                                        >
                                                            Edit
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className="projectsTableActionsDelete">
                                                    {!isGuest && <Tooltip label="Delete project">
                                                        <ActionIcon
                                                            variant="light"
                                                            color="gray"
                                                            radius={8}
                                                            styles={{
                                                                root: {
                                                                    transition: "background-color 120ms ease, color 120ms ease",
                                                                    "&:hover": { backgroundColor: isDark ? "#7f1d1d" : "#fee2e2", color: isDark ? "#fca5a5" : "#b91c1c" },
                                                                },
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                handleDeleteClick(row.projectKey)
                                                            }}
                                                        >
                                                            <DeleteIcon size={20} />
                                                        </ActionIcon></Tooltip>}
                                                </div>
                                                {!isGuest && handleCopySubmit && !isLimitedPlan && (
                                                <div className="projectsTableActionsDuplicate">
                                                    <Tooltip label="Duplicate project"><ActionIcon
                                                        variant="light"
                                                        color="gray"
                                                        radius={8}
                                                        styles={{
                                                            root: {
                                                                transition: "background-color 120ms ease, color 120ms ease",
                                                                "&:hover": { backgroundColor: isDark ? "#1e3a8a" : "#e0e7ff", color: isDark ? "#93c5fd" : "#3730a3" },
                                                            },
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            handleCopySubmit(row.projectKey)
                                                        }}
                                                    >
                                                        <IconCopy size={20} />
                                                    </ActionIcon></Tooltip>
                                                </div>
                                                )}
                                            </div>
                                        </Card>
                                    </div></Suspense>
                            ))}
                          </div>
                        </div>
                    </form>
                </div>
            </Suspense>
        </Suspense>
        <Canvas
            eventSource={document.getElementById("root")}
            className="canvas"
            shadows
            style={hiddenCanvas}
            camera={{
                position: [0, 0, 0],
                fov: 55
            }}
            frameloop="demand"
            gl={{ preserveDrawingBuffer: true }}
            ref={canvasRef}
        >
            <View.Port />
        </Canvas>
    </div>
    );
}



